import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Inbox, Send, Sparkles, Loader2, RefreshCw, Mail, Phone, MapPin, CheckCircle2, Bot, FolderOpen, FileText, CalendarDays, Receipt } from 'lucide-react';
import { formatDatePT } from '../utils/dateFormat';

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('nieuw');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState({});
  const [dossiers, setDossiers] = useState({});
  const [dossierLoading, setDossierLoading] = useState({});
  const navigate = useNavigate();

  const toggleDossier = async (lead) => {
    if (dossiers[lead.id]) {
      setDossiers((d) => ({ ...d, [lead.id]: null }));
      return;
    }
    setDossierLoading((s) => ({ ...s, [lead.id]: true }));
    try {
      const r = await axiosInstance.get(`/leads/${lead.id}/dossier`);
      setDossiers((d) => ({ ...d, [lead.id]: r.data }));
    } catch {
      toast.error('Dossier laden mislukt');
    } finally {
      setDossierLoading((s) => ({ ...s, [lead.id]: false }));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/leads${filter !== 'alles' ? `?status=${filter}` : ''}`);
      setLeads(r.data.leads || []);
    } catch {
      toast.error('Kon aanvragen niet laden');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const scanMail = async () => {
    setScanning(true);
    try {
      const r = await axiosInstance.post('/leads/scan', {}, { timeout: 120000 });
      const n = r.data.leads_found || 0;
      if (n > 0) toast.success(`${n} nieuwe aanvra(a)g(en) gevonden in je mailbox`);
      else toast.info('Mailbox gecontroleerd — geen nieuwe aanvragen gevonden');
      (r.data.errors || []).forEach((er) => toast.error(`${er.account}: ${er.error}`));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mailbox controleren mislukt');
    } finally {
      setScanning(false);
      load();
    }
  };

  const sendReply = async (lead) => {
    const message = (drafts[lead.id] !== undefined ? drafts[lead.id] : lead.ai_draft) || '';
    if (!message.trim()) return toast.error('Schrijf eerst een antwoord');
    setSending((s) => ({ ...s, [lead.id]: true }));
    try {
      const r = await axiosInstance.post(`/leads/${lead.id}/reply`, { message: message.trim() });
      toast.success(`Antwoord verstuurd naar ${r.data.sent_to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setSending((s) => ({ ...s, [lead.id]: false }));
    }
  };

  const toQuote = (lead) => {
    const prompt = `Maak van deze offerte-aanvraag een concept-offerte met realistische werkregels en stel daarna ook een agenda-afspraak voor een vrijblijvende opname voor. Aanvraag van ${lead.name || 'onbekend'}${lead.address ? `, adres: ${lead.address}` : ''}${lead.phone ? `, tel: ${lead.phone}` : ''}${lead.email ? `, e-mail: ${lead.email}` : ''}. De vraag: ${lead.message}`;
    navigate(`/assistent?prompt=${encodeURIComponent(prompt)}`);
  };

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-[#1a5276]" /> Aanvragen
          </h1>
          <p className="text-gray-500 text-sm">Offerte-aanvragen uit je e-mail — met kant-en-klaar AI-antwoord</p>
        </div>
        <div className="flex gap-2">
          {['nieuw', 'beantwoord', 'alles'].map((f) => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
              className={filter === f ? 'bg-[#1a5276] hover:bg-[#154360]' : ''}
              onClick={() => setFilter(f)} data-testid={`leads-filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={scanMail} disabled={scanning} className="border-[#1a5276] text-[#1a5276]" data-testid="leads-scan-mail-btn">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} {scanning ? 'Mailbox controleren...' : 'Check mailbox'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Laden...</div>
      ) : leads.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="py-12 text-center text-gray-400 text-sm">
            <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            Geen {filter !== 'alles' ? `${filter}e` : ''} aanvragen.<br />
            Nieuwe aanvragen uit je e-mail verschijnen hier automatisch na elke mailbox-sync.
          </CardContent>
        </Card>
      ) : (
        leads.map((lead) => (
          <Card key={lead.id} className="bg-white border-gray-200" data-testid={`lead-card-${lead.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-gray-800">{lead.name || 'Onbekende aanvrager'}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${lead.status === 'nieuw' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {lead.status === 'nieuw' ? 'NIEUW' : 'BEANTWOORD'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mt-1">
                    {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                    {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                    {lead.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.address}</span>}
                  </div>
                </div>
                <p className="text-xs text-gray-400">{fmtWhen(lead.created_at)}</p>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded p-3 text-sm text-gray-700">
                <p className="text-[11px] font-bold text-gray-400 mb-1">VRAAG VAN DE KLANT</p>
                {lead.message}
              </div>

              <Button size="sm" variant="ghost" onClick={() => toggleDossier(lead)} className="text-[#1a5276]" data-testid={`lead-dossier-btn-${lead.id}`}>
                {dossierLoading[lead.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FolderOpen className="w-4 h-4 mr-1" />}
                {dossiers[lead.id] ? 'Dossier verbergen' : 'Klantdossier bekijken'}
              </Button>

              {dossiers[lead.id] && (
                <div className="border border-[#1a5276]/20 rounded-lg p-3 space-y-3 bg-[#1a5276]/[0.03]" data-testid={`lead-dossier-${lead.id}`}>
                  <div>
                    <p className="text-[11px] font-bold text-[#1a5276] mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> OFFERTES ({dossiers[lead.id].quotes.length})</p>
                    {dossiers[lead.id].quotes.length === 0 ? <p className="text-xs text-gray-400 italic">Geen offertes gevonden</p> : (
                      dossiers[lead.id].quotes.map((q) => (
                        <button key={q.id} onClick={() => navigate('/quotes')} className="block w-full text-left text-xs text-gray-700 hover:text-[#1a5276] py-0.5">
                          {q.number} · {q.client_name} · € {(q.total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })} · <span className="uppercase font-semibold">{q.status}</span>
                        </button>
                      ))
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-[#1a5276] mb-1 flex items-center gap-1"><Receipt className="w-3 h-3" /> FACTUREN ({dossiers[lead.id].invoices.length})</p>
                    {dossiers[lead.id].invoices.length === 0 ? <p className="text-xs text-gray-400 italic">Geen facturen gevonden</p> : (
                      dossiers[lead.id].invoices.map((inv) => (
                        <button key={inv.id} onClick={() => navigate('/invoices')} className="block w-full text-left text-xs text-gray-700 hover:text-[#1a5276] py-0.5">
                          {inv.number} · € {(inv.total_incl || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })} · <span className="uppercase font-semibold">{inv.status}</span>
                        </button>
                      ))
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-[#1a5276] mb-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> AFSPRAKEN ({dossiers[lead.id].schedules.length})</p>
                    {dossiers[lead.id].schedules.length === 0 ? <p className="text-xs text-gray-400 italic">Geen afspraken gevonden</p> : (
                      dossiers[lead.id].schedules.map((s) => (
                        <button key={s.id} onClick={() => navigate('/schedule')} className="block w-full text-left text-xs text-gray-700 hover:text-[#1a5276] py-0.5">
                          {formatDatePT(s.date)} {s.time && `om ${s.time}`} · {s.client_name} {s.work_description && `· ${s.work_description.slice(0, 50)}`}
                        </button>
                      ))
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-[#1a5276] mb-1">FOTO'S ({dossiers[lead.id].photos.length})</p>
                    {dossiers[lead.id].photos.length === 0 ? <p className="text-xs text-gray-400 italic">Geen foto's gevonden</p> : (
                      <div className="flex flex-wrap gap-2">
                        {dossiers[lead.id].photos.map((ph, i) => (
                          <a key={i} href={ph.url} target="_blank" rel="noreferrer">
                            <img src={ph.thumbnail} alt={`Foto ${i + 1}`} className="w-16 h-16 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {lead.status === 'nieuw' ? (
                <>
                  <div>
                    <p className="text-[11px] font-bold text-[#1a5276] mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI-CONCEPTANTWOORD (pas gerust aan)</p>
                    <textarea
                      value={drafts[lead.id] !== undefined ? drafts[lead.id] : (lead.ai_draft || '')}
                      onChange={(e) => setDrafts((d) => ({ ...d, [lead.id]: e.target.value }))}
                      rows={6}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a5276]/30"
                      data-testid={`lead-draft-${lead.id}`}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => sendReply(lead)} disabled={sending[lead.id]} className="bg-[#1a5276] hover:bg-[#154360]" data-testid={`lead-send-${lead.id}`}>
                      {sending[lead.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Verstuur antwoord
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toQuote(lead)} className="border-[#3da5a5] text-[#2d8585]" data-testid={`lead-to-quote-${lead.id}`}>
                      <Bot className="w-4 h-4 mr-1" /> Offerte + agenda via Assistent
                    </Button>
                  </div>
                </>
              ) : (
                <div className="bg-green-50 border border-green-100 rounded p-3 text-sm text-gray-700">
                  <p className="text-[11px] font-bold text-green-700 mb-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> VERSTUURD ANTWOORD {lead.replied_at && `· ${formatDatePT(lead.replied_at.slice(0, 10))}`}</p>
                  <p className="whitespace-pre-wrap">{lead.reply_message}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
